// ViewToggle — the page-header segmented control that swaps the whole
// schedule view body (Calendar ↔ Density). Mirrors TableViewToggle on
// the milestone surface verbatim: bordered container, leading icon per
// option, role=radiogroup + per-button role=radio. The single justified
// brand-violet on the page header (preserves the violet-density rule).
// Clicking the already-active option is a no-op.

import { clsx } from "clsx";
import { Calendar, LayoutGrid } from "lucide-react";

export type ScheduleViewValue = "calendar" | "density";

interface ViewToggleProps {
  value: ScheduleViewValue;
  onChange: (next: ScheduleViewValue) => void;
}

const OPTIONS: ReadonlyArray<{
  value: ScheduleViewValue;
  label: string;
  Icon: typeof Calendar;
}> = [
  { value: "calendar", label: "Calendar", Icon: Calendar },
  { value: "density", label: "Density", Icon: LayoutGrid },
];

export default function ViewToggle({ value, onChange }: ViewToggleProps) {
  return (
    <div
      role="radiogroup"
      aria-label="Schedule view"
      data-testid="schedule-view-toggle"
      className="inline-flex rounded-md border border-slate-200 bg-white p-0.5 dark:border-slate-700 dark:bg-slate-800"
    >
      {OPTIONS.map(({ value: optionValue, label, Icon }) => {
        const active = value === optionValue;
        return (
          <button
            key={optionValue}
            type="button"
            role="radio"
            aria-checked={active}
            aria-pressed={active}
            data-testid={`schedule-view-${optionValue}`}
            onClick={() => {
              if (!active) onChange(optionValue);
            }}
            className={clsx(
              "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-caption font-medium transition-colors focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400",
              active
                ? "bg-brand-50 text-brand-700 dark:bg-brand-950/40 dark:text-brand-300"
                : "text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200",
            )}
          >
            <Icon className="h-3.5 w-3.5" aria-hidden="true" />
            <span>{label}</span>
          </button>
        );
      })}
    </div>
  );
}
